// src/services/firebase.ts
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  signInWithRedirect,
  getRedirectResult,
  connectAuthEmulator
} from 'firebase/auth';
import { User } from '../types';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Connect to Firebase Auth Emulator if the environment variable is set
if (process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true' && 
    process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST) {
  console.log(`Connecting to Firebase Auth emulator at ${process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST}`);
  connectAuthEmulator(
    auth, 
    `http://${process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST}`,
    { disableWarnings: false }
  );
}

/**
 * Checks if a user has admin role from Firebase custom claims
 * @param user Firebase user object
 * @returns Promise that resolves to boolean indicating if user has admin role
 */
export const hasAdminRole = async (user: FirebaseUser): Promise<boolean> => {
  try {
    // Get the ID token with fresh claims
    const token = await user.getIdTokenResult(true);
    
    // Check for admin role in the roles array (case insensitive)
    const roles = token.claims.roles;
    
    if (Array.isArray(roles)) {
      // Check case-insensitively for any variation of "admin"
      const hasAdmin = roles.some(role => 
        typeof role === 'string' && role.toLowerCase() === 'admin'
      );
      
      return hasAdmin;
    }
    
    // No roles array found
    return false;
  } catch (error) {
    console.error('Error checking admin role:', error);
    return false;
  }
};

/**
 * Signs in a user with Google authentication using popup
 * @returns Promise<User> - always returns a fully typed User object
 */
export const signInWithGoogle = async (): Promise<User> => {
  const provider = new GoogleAuthProvider();
  
  // Force account selection
  provider.setCustomParameters({
    prompt: 'select_account'
  });

  try {
    const result = await signInWithPopup(auth, provider);

    // always retrieve a fresh token
    await result.user.getIdToken(true);

    // convert to our app's User type (role + isAdmin included)
    return await convertFirebaseUser(result.user);

  } catch (error: any) {
    console.error('Error in Google sign-in:', error);
    throw error; // keep throwing so UI can display login error
  }
};


/**
 * Handles the redirect result from Google sign-in
 * Should be called on app initialization
 * @returns Promise that resolves when redirect result is processed
 */
export const handleRedirectResult = async (): Promise<User | null> => {
  try {
    // Get the redirect result
    const result = await getRedirectResult(auth);
    
    // If no result, user hasn't been redirected from auth provider
    if (!result) {
      // Check if user is already signed in
      if (auth.currentUser) {
        return await convertFirebaseUser(auth.currentUser);
      }
      return null;
    }
    
    console.log('Got redirect result:', result.user?.email);
    
    // Convert to our User type regardless of admin status
    return await convertFirebaseUser(result.user);
  } catch (error: any) {
    console.error('Error processing redirect result:', error);
    // If this is a Firebase auth error, provide a clearer message
    if (error.code && error.code.startsWith('auth/')) {
      throw new Error(`Authentication error: ${error.message}`);
    }
    
    // Rethrow other errors
    throw error;
  }
};

/**
 * Signs in a user with email and password
 * @param email User's email
 * @param password User's password
 * @returns Promise<User> containing our app's User object
 * @throws Error if the login fails
 */
export const signInWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    // Perform the sign in
    const result = await signInWithEmailAndPassword(auth, email, password);
    
    // Refresh the token
    await result.user.getIdToken(true);

    // Convert Firebase user → our User
    const user = await convertFirebaseUser(result.user);

    return user;

  } catch (error: any) {
    console.error('Error signing in with email/password:', error);
    throw error;
  }
};

/**
 * Creates a new user with email and password
 * @param email User's email
 * @param password User's password
 * @returns Promise that resolves when user creation is complete
 * @throws Error if the user is not authorized
 */
export const createUserWithEmail = async (email: string, password: string): Promise<void> => {
  // Check if the email is authorized before creating the user
  // NOTE: This check is not possible without first creating the user and then checking the custom claims
  //       However, this is not a security risk since the user will be signed out immediately if they don't have the admin role
  try {
    await createUserWithEmailAndPassword(auth, email, password);
  } catch (error) {
    console.error('Error creating user with email/password:', error);
    throw error;
  }
};

/**
 * Signs out the current user
 * @returns Promise that resolves when sign-out is complete
 */
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};


export const convertFirebaseUser = async (firebaseUser: FirebaseUser): Promise<User> => {
  const token = await firebaseUser.getIdToken();
  const decodedToken = await firebaseUser.getIdTokenResult(true);

  // Extract roles from Firebase custom claims (backend sets roles as an array)
  const roles = decodedToken.claims.roles as string[] | undefined;
  
  // Check if user has Admin or Manager role in the roles array
  let role: "Admin" | "Manager" | "User" = "User";
  if (Array.isArray(roles)) {
    if (roles.some(r => typeof r === 'string' && r === 'Admin')) {
      role = "Admin";
    } else if (roles.some(r => typeof r === 'string' && r === 'Manager')) {
      role = "Manager";
    }
  }

  return {
    uid: firebaseUser.uid,
    displayName: firebaseUser.displayName,
    email: firebaseUser.email,
    token,
    role,
    isAdmin: role === "Admin",
  };
};


/**
 * Sets up an auth state observer and returns a cleanup function
 * @param callback Function to call when auth state changes
 * @returns Function to unsubscribe the observer
 */
export const observeAuthState = (
  callback: (user: User | null) => void
): (() => void) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      // Check authorization but don't sign out non-admins
      const isAdmin = await hasAdminRole(firebaseUser);
      
      const user = await convertFirebaseUser(firebaseUser);
      callback(user);
    } else {
      callback(null);
    }
  });
};

/**
 * Refreshes the authentication token for the current user
 * @returns Promise containing the new token or null if no user is signed in
 */
export const refreshAuthToken = async (): Promise<string | null> => {
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    console.warn('Cannot refresh token: No user is currently signed in');
    return null;
  }
  
  try {
    // Force token refresh (don't use cached token)
    const newToken = await currentUser.getIdToken(true);
    return newToken;
  } catch (error) {
    console.error('Error refreshing authentication token:', error);
    return null;
  }
};